import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchWithAuth, isAuthenticated } from "../utils/fetchWithAuth";
import { trackPageView, identify } from "../lib/tracker";
import { redactPath } from "../lib/tokenisedPaths";

/**
 * Fires on every route change:
 *   1. First-party clickstream page_view (anonymous AND logged-in) — captures
 *      referrer + UTM so we can see where every visitor came from.
 *   2. For logged-in users: identity stitch (once/session) + the legacy
 *      /track-visit beacon that powers DAU/WAU/MAU (PageVisit table).
 */
export default function usePageVisitTracker() {
  const { pathname } = useLocation();
  const prev = useRef(null);

  useEffect(() => {
    if (pathname === prev.current) return;
    prev.current = pathname;

    // First-party page_view — fires for everyone, before auth.
    trackPageView(pathname);

    if (!isAuthenticated()) return;

    // Link this visitor's pre-account journey to their user (idempotent).
    identify();

    // Legacy DAU/WAU/MAU beacon — platform metrics still reads PageVisit.
    // Redacted like everything else: this one writes to a different table, so
    // scrubbing only the clickstream would have left the same credential in
    // PageVisit whenever a logged-in user opened a signing or payment link.
    fetchWithAuth("/api/accounts/track-visit/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: redactPath(pathname) }),
    }).catch(() => {});
  }, [pathname]);
}
